import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, 
  Target, 
  Globe, 
  Users, 
  Lightbulb,
  Rocket,
  TrendingUp,
  Star,
  Heart
} from 'lucide-react';

export default function VisionPage() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background border-b">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0))]" style={{ backgroundSize: '30px 30px' }} />
        <div className="relative container px-4 md:px-6 flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] gap-4 py-12 text-center">
          <Badge variant="outline" className="mb-2">Our Vision</Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl mb-4">
            Building the Future of{' '}
            <span className="text-primary">Professional Growth</span>
          </h1>
          <p className="max-w-[600px] text-muted-foreground md:text-xl dark:text-gray-400 mb-5">
            Our mission is to transform how professionals connect, grow, and succeed in their careers
          </p>
          <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
            <Button asChild size="lg" className="text-base">
              <Link to="/register">
                Join Our Mission
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-base">
              <Link to="/about/company">
                Meet Our Team
                <Users className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Vision Grid */}
      <section className="py-20 px-4 md:px-6">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">Our Vision</h2>
            <p className="mt-4 text-muted-foreground md:text-lg">
              We're building a platform that empowers professionals to reach their full potential
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Global Connection</h3>
                <p className="text-muted-foreground">
                  Breaking down barriers to connect professionals worldwide, fostering collaboration and growth across borders.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Lightbulb className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Innovation First</h3>
                <p className="text-muted-foreground">
                  Leveraging cutting-edge technology to create smarter, more efficient ways of professional networking and growth.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Heart className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Human-Centered</h3>
                <p className="text-muted-foreground">
                  Putting people first in everything we do, creating meaningful connections and opportunities for growth.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Continuous Growth</h3>
                <p className="text-muted-foreground">
                  Empowering professionals with tools and insights for continuous learning and career advancement.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Star className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Excellence</h3>
                <p className="text-muted-foreground">
                  Setting the highest standards in professional networking and career development tools.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Rocket className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Future Ready</h3>
                <p className="text-muted-foreground">
                  Preparing professionals for the evolving landscape of work with forward-thinking solutions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 px-4 md:px-6 bg-primary text-primary-foreground">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
              Our Mission
            </h2>
            <p className="mt-4 text-primary-foreground/80 md:text-xl">
              To empower professionals worldwide with the tools, connections, and opportunities they need to thrive in their careers.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-base"
                asChild
              >
                <Link to="/register">
                  Join Our Community
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-transparent border-white hover:bg-white/10 text-base"
                asChild
              >
                <Link to="/about/values">
                  Our Values
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}